import { DocumentNode, useMutation } from "@apollo/client";
import {
  InputControl,
  InputPasswordControl,
  PasswordList,
  usePasswordPolicy,
  usePlatformConfig,
} from "@aurem/components";
import { Button, Text } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContainer } from "components/AuthContainer";
import { useAcceptInvitation } from "hooks/useAcceptInvitation";
import useAnalytics, { AnalyticsEvent } from "hooks/useAnalytics";
import { useNavigateAfterInvitation } from "hooks/useNavigateAfterInvitation";
import { useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { gql } from "types";
import { z } from "zod";
import ROUTES from "../../../const/routes";
import { useSignOut } from "../../../utils/auth";

const SIGN_UP_EMPLOYEE = gql(/* GraphQL */ `
  mutation RegisterEmployee(
    $email: String!
    $password: String!
    $isEmployee: Boolean!
    $invitationId: String
  ) {
    registerUser(
      email: $email
      password: $password
      isEmployee: $isEmployee
      invitationId: $invitationId
    ) {
      accessToken
      refreshToken
      userId
    }
  }
`);

export const SignUpEmployee = () => {
  const { t, language } = useI18n();
  const [searchParams] = useSearchParams();
  const { language: languageParam } = useParams();
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  const config = usePlatformConfig();
  const { validatePassword, policy } = usePasswordPolicy();
  const { track } = useAnalytics();
  const { acceptInvitation } = useAcceptInvitation();
  const getDestination = useNavigateAfterInvitation();
  const signOut = useSignOut();

  const email = searchParams.get("email") ?? "";
  const invitationId = searchParams.get("invitationId");

  const schema = useMemo(() => {
    return z
      .object({
        workEmail: z
          .string()
          .nonempty(t("apps.employee.validation.required"))
          .email({ message: t("apps.employee.validation.email.format") }),
        personalEmail: z
          .string()
          .nonempty(t("apps.employee.validation.required"))
          .email({ message: t("apps.employee.validation.email.format") }),
        password: z
          .string()
          .nonempty(t("apps.employee.validation.password.required")),
        confirmPassword: z
          .string()
          .nonempty(t("apps.employee.validation.password.required")),
      })
      .refine((data) => data.password === data.confirmPassword, {
        message: t("apps.employee.validation.password.passwordNotMatch"),
        path: ["confirmPassword"],
      });
  }, [t]);

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      workEmail: email,
      personalEmail: "",
      password: "",
      confirmPassword: "",
    },
  });

  const { handleSubmit, setError } = methods;

  const [signUp, { loading, error }] = useMutation(
    SIGN_UP_EMPLOYEE as DocumentNode,
    {
      context: {
        headers: {
          "Accept-Language": language,
        },
      },
    },
  );

  const onSubmit = async ({
    personalEmail,
    password,
  }: {
    personalEmail: string;
    password: string;
    confirmPassword: string;
  }) => {
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError("password", {
        type: "manual",
        message: passwordError,
      });
      return;
    }

    await signOut();
    let data;
    try {
      ({ data } = await signUp({
        variables: {
          email: personalEmail,
          password,
          isEmployee: true,
          invitationId,
        },
      }));
    } catch {
      return;
    }

    const tokens = data?.registerUser ?? null;
    if (tokens !== null) {
      setAuthState(tokens);
    }

    track(AnalyticsEvent.SignUpByEmail);

    const invitationData = await acceptInvitation({
      accessToken: tokens?.accessToken,
    });

    const from = invitationData
      ? getDestination({ invitationData })
      : undefined;

    void navigate(`/${languageParam}/employee/${ROUTES.DISCLAIMERS}`, {
      state: { from },
    });
  };

  return (
    <AuthContainer platform="employee">
      <div>
        <h2 className="font-medium text-2xl">
          {t("apps.employee.pages.signUp.page.title", config.name)}
        </h2>
      </div>

      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-6"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-5">
            <InputControl
              type="email"
              label={t("apps.employee.pages.signUp.page.workEmail")}
              name="workEmail"
              required
              disabled
              data-e2e-id="signUpEmployee__input__workEmail"
            />
            <InputControl
              type="email"
              label={t("apps.employee.pages.signUp.page.personalEmail")}
              name="personalEmail"
              description={t("apps.employee.pages.signUp.page.description")}
              required
              autoComplete="email"
              data-e2e-id="signUpEmployee__input__personalEmail"
            />

            <InputPasswordControl
              label={t("apps.employee.pages.signUp.page.newPassword")}
              name="password"
              required
              autoComplete="new-password"
              data-e2e-id="signUpEmployee__input__password"
            />

            <InputPasswordControl
              label={t("apps.employee.pages.signUp.page.repeat")}
              name="confirmPassword"
              required
              autoComplete="new-password"
              data-e2e-id="signUpEmployee__input__confirmPassword"
            />

            {error && (
              <Text size="sm" weight="medium" className="text-danger-600">
                {error.message}
              </Text>
            )}
          </div>

          <PasswordList
            policy={policy}
            translations={{
              intro: t("apps.employee.validation.passwordPolicy.list.intro"),
              capitalLetter: t(
                "apps.employee.validation.passwordPolicy.list.capitalLetter",
              ),
              lowercaseLetter: t(
                "apps.employee.validation.passwordPolicy.list.lowercaseLetter",
              ),
              number: t("apps.employee.validation.passwordPolicy.list.number"),
              specialCharacter: t(
                "apps.employee.validation.passwordPolicy.list.specialCharacter",
              ),
              minimumLength: t(
                "apps.employee.validation.passwordPolicy.list.minimumLength",
                policy?.minLength,
              ),
            }}
          />

          <Button
            className="w-full"
            loading={loading}
            type="submit"
            data-e2e-id="signUpEmployee__button__submit"
          >
            {loading
              ? t("apps.employee.common.loading")
              : t("apps.employee.pages.signUp.page.cta")}
          </Button>
        </form>
      </FormProvider>
    </AuthContainer>
  );
};

export default SignUpEmployee;
